import React from 'react';
import {
  Star, ShoppingCart, Gem, Megaphone, Database, BadgeCheck,
  ShoppingBag, Briefcase, Crown, Coins, Wand2, MonitorPlay,
  Rocket, ArrowRight, ArrowUpRight, Quote,
} from 'lucide-react';
import { CLIENT_SUCCESS, TEMPLE_SUCCESS } from '../data/siteData';
import useReveal from '../hooks/useReveal';

const ICONS = {
  Star, ShoppingCart, Gem, Megaphone, Database, BadgeCheck,
  ShoppingBag, Briefcase, Crown, Coins, Wand2, MonitorPlay,
};

function Stars({ count, dim }) {
  return (
    <div className={`success-stars ${dim ? 'success-stars-dim' : ''}`} aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={17} className={i <= count ? 'star-filled' : 'star-empty'} />
      ))}
    </div>
  );
}

function AchievementCard({ item, index }) {
  const ref = useReveal();
  const Icon = ICONS[item.icon];
  return (
    <article
      ref={ref}
      className={`success-card reveal ${item.featured ? 'success-card-featured' : ''}`}
      style={{ transitionDelay: `${(index % 3) * 90}ms` }}
      data-testid={`success-card-${item.id}`}
    >
      <div className="success-card-icon"><Icon size={22} /></div>
      <h3 className="success-card-title">{item.title}</h3>
      <p className="success-card-desc">{item.description}</p>
    </article>
  );
}

function ReviewCompare({ compareRef, cs }) {
  return (
    <div className="success-compare reveal" ref={compareRef} data-testid="success-compare">
      <div className="success-compare-card success-compare-before">
        <span className="success-compare-year">{cs.before.year}</span>
        <span className="success-compare-rating">{cs.before.rating}</span>
        <Stars count={3} dim />
        <span className="success-compare-reviews">{cs.before.reviews} on Google</span>
      </div>
      <div className="success-compare-mid" aria-hidden="true">
        <div className="success-trajectory">
          <Rocket size={30} className="success-rocket" />
        </div>
        <span className="success-compare-label">BOOST! APPLIED</span>
      </div>
      <div className="success-compare-card success-compare-after">
        <span className="success-compare-year">{cs.after.year}</span>
        <span className="success-compare-rating">{cs.after.rating}</span>
        <Stars count={5} />
        <span className="success-compare-reviews">{cs.after.reviews} on Google</span>
        <span className="success-compare-badge">Among the best in the industry</span>
      </div>
    </div>
  );
}

function SuccessStats({ statsRef, stats }) {
  return (
    <div className="success-stats reveal" ref={statsRef} data-testid="success-stats">
      {stats.map((s) => (
        <div key={s.label} className="success-stat">
          <span className="success-stat-value">{s.value}</span>
          <span className="success-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function BrowserShot({ src, alt, caption, url }) {
  return (
    <figure className="browser-shot">
      <div className="browser-chrome">
        <span className="browser-dot" />
        <span className="browser-dot" />
        <span className="browser-dot" />
        <span className="browser-url">{url}</span>
      </div>
      <img src={src} alt={alt} loading="lazy" />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function TempleBlock() {
  const headRef = useReveal();
  const statsRef = useReveal();
  const ctaRef = useReveal();
  const ts = TEMPLE_SUCCESS;

  const goContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="temple-block" data-testid="temple-success">
      <div className="section-head reveal" ref={headRef}>
        <span className="section-eyebrow">{ts.code}</span>
        <h3 className="section-title temple-title">
          TEMPLE OF SOUND: A <span className="text-pink">BESPOKE RETAIL HUB</span>
        </h3>
        <p className="section-sub">{ts.intro}</p>
        <span className="success-industry">{ts.industry}</span>
      </div>

      <div className="success-stats reveal" ref={statsRef} data-testid="temple-stats">
        {ts.stats.map((s) => (
          <div key={s.label} className="success-stat">
            <span className="success-stat-value">{s.value}</span>
            <span className="success-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="temple-shots" data-testid="temple-screenshots">
        <BrowserShot
          src="/temple/home.jpg"
          alt="Temple of Sound storefront homepage"
          caption="The storefront — two brands, one house"
          url="templeofsound.co.uk"
        />
        <BrowserShot
          src="/temple/shop.jpg"
          alt="Alchemist Cymbals shop with series filtering"
          caption="B2C shop — 15K+ handcrafted cymbals"
          url="templeofsound.co.uk/shop"
        />
        <BrowserShot
          src="/temple/builder.jpg"
          alt="Custom drumstick builder configurator"
          caption="Creative studio — custom drumstick builder"
          url="templeofsound.co.uk/custom-drumsticks"
        />
      </div>

      <div className="success-grid">
        {ts.features.map((f, i) => (
          <AchievementCard key={f.id} item={f} index={i} />
        ))}
      </div>

      <div className="temple-ctas reveal" ref={ctaRef}>
        <a
          className="boost-btn boost-btn-ghost boost-btn-lg"
          href={ts.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="temple-visit-link"
        >
          Visit the live site
          <ArrowUpRight size={18} />
        </a>
        <button className="boost-btn boost-btn-primary boost-btn-lg" onClick={goContact} data-testid="temple-cta">
          Build Something This Ambitious
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default function ClientSuccess() {
  const headRef = useReveal();
  const compareRef = useReveal();
  const statsRef = useReveal();
  const quoteRef = useReveal();
  const cs = CLIENT_SUCCESS;

  const goContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="section success-section" id="success" aria-label="Client success story: Diamondbrite" data-testid="success-section">
      <div className="section-inner">
        <div className="section-head reveal" ref={headRef}>
          <span className="section-eyebrow">// MISSION REPORT 01: CLIENT SUCCESS</span>
          <h2 className="section-title">
            DIAMONDBRITE: A BRAND IN <span className="text-pink">FULL ORBIT</span>
          </h2>
          <p className="section-sub">{cs.intro}</p>
          <span className="success-industry" data-testid="success-industry">{cs.industry}</span>
        </div>

        <ReviewCompare compareRef={compareRef} cs={cs} />

        <SuccessStats statsRef={statsRef} stats={cs.stats} />

        {/* Achievements grid */}
        <div className="success-grid">
          {cs.achievements.map((a, i) => (
            <AchievementCard key={a.id} item={a} index={i} />
          ))}
        </div>

        {/* Quote + CTA */}
        <div className="success-quote reveal" ref={quoteRef} data-testid="success-quote">
          <Quote size={30} className="success-quote-mark" />
          <p className="success-quote-text">{cs.quote}</p>
          <button className="boost-btn boost-btn-primary boost-btn-lg" onClick={goContact} data-testid="success-cta">
            Make Your Brand Our Next Success Story
            <ArrowRight size={18} />
          </button>
        </div>

        <TempleBlock />
      </div>
    </section>
  );
}
